import { ArrowUturnLeftIcon, ListBulletIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { runAi } from '../../data/ai';
import { diffWords } from '../../data/diff';
import { describeApiError } from '../../data/http';

interface AiAssistProps {
  value: string;
  onChange: (value: string) => void;
}

const control =
  'inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * SV-08/09/10. Improve rewrites in place and can be undone; generate and outline
 * are previewed first, because writing into an empty field has nothing to
 * compare against and nothing to undo back to.
 */
export function AiAssist({ value, onChange }: AiAssistProps) {
  const { t } = useTranslation();

  const [previous, setPrevious] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasText = value.trim().length > 0;

  const improve = async () => {
    setBusy(true);
    setError(null);
    try {
      const { original, result } = await runAi('improve-text', value);
      if (result.trim()) {
        setPrevious(original);
        onChange(result);
      }
    } catch (err) {
      setError(describeApiError(err, t('ai.failed')));
    } finally {
      setBusy(false);
    }
  };

  const propose = async (action: 'generate-text' | 'outline') => {
    const source = hasText ? value : instruction;
    if (!source.trim()) return;

    setBusy(true);
    setError(null);
    try {
      const { result } = await runAi(action, source, hasText ? instruction : undefined);
      if (result.trim()) setPreview(result);
    } catch (err) {
      setError(describeApiError(err, t('ai.failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1">
      {!hasText ? (
        <input
          type="text"
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          placeholder={t('ai.instructionPlaceholder')}
          aria-label={t('ai.instructionLabel')}
          className="mb-2 w-full rounded-sm border border-input bg-surface px-3 py-1.5 text-xs outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {hasText ? (
          <button type="button" onClick={() => void improve()} disabled={busy} className={control}>
            <SparklesIcon className="size-4" aria-hidden="true" />
            {busy ? t('ai.working') : t('ai.improve')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void propose('generate-text')}
            disabled={busy || !instruction.trim()}
            className={control}
          >
            <SparklesIcon className="size-4" aria-hidden="true" />
            {busy ? t('ai.working') : t('ai.generate')}
          </button>
        )}

        <button
          type="button"
          onClick={() => void propose('outline')}
          disabled={busy || (!hasText && !instruction.trim())}
          className={control}
        >
          <ListBulletIcon className="size-4" aria-hidden="true" />
          {t('ai.outline')}
        </button>

        {previous !== null ? (
          <button
            type="button"
            onClick={() => {
              onChange(previous);
              setPrevious(null);
            }}
            className={control}
          >
            <ArrowUturnLeftIcon className="size-4" aria-hidden="true" />
            {t('ai.undo')}
          </button>
        ) : null}
      </div>

      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}

      {preview !== null ? (
        <div className="mt-2 rounded border border-border bg-muted p-3">
          <p className="text-xs font-medium text-muted-foreground">{t('ai.preview')}</p>
          <pre className="mt-1 whitespace-pre-wrap break-words font-body text-sm text-foreground">{preview}</pre>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setPrevious(value);
                onChange(value.trim() ? `${value}\n\n${preview}` : preview);
                setPreview(null);
              }}
              className={control}
            >
              {t('ai.insert')}
            </button>
            <button type="button" onClick={() => setPreview(null)} className={control}>
              {t('ai.discard')}
            </button>
          </div>
        </div>
      ) : null}

      {/* A diff of "" against new text is entirely additions, so it is only shown when something was replaced. */}
      {previous !== null && previous.trim().length > 0 && preview === null ? (
        <div className="mt-2 rounded border border-border bg-muted p-3">
          <p className="text-xs font-medium text-muted-foreground">{t('ai.changes')}</p>
          <p className="mt-1 text-sm leading-loose">
            {diffWords(previous, value).map((part, index) =>
              part.kind === 'same' ? (
                <span key={index} className="text-foreground">
                  {part.text}
                </span>
              ) : part.kind === 'added' ? (
                // Padded so a replacement does not read as one run-on word.
                <ins key={index} className="mx-0.5 rounded-sm bg-accent px-1 text-accent-foreground no-underline">
                  {part.text.trim()}
                </ins>
              ) : (
                <del key={index} className="mx-0.5 rounded-sm bg-destructive-soft px-1 text-destructive">
                  {part.text.trim()}
                </del>
              )
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
